import { parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import {
  data,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
  useFetcher,
} from 'react-router'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import InvalidCodeForm from '#app/components/invalid-code-form.tsx'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import { prisma } from '#app/utils/db.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getDomainUrl } from '#app/utils/misc.js'
import { prepareVerification } from '#app/utils/otp.server.ts'
import { sendResetPasswordEmail } from '#app/utils/reset-password.tsx'
import { Status } from '#app/utils/status.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'

const InvalidCodeSchema = z.object({
  email: EmailSchema,
})

export async function loader({ context }: LoaderFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const pageTitle = t('invalidCode.title')

  return { pageTitle }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.pageTitle ?? 'Error'
  return [{ title: `${pageTitle} | ${siteTitle}` }]
}

export async function action({ context, request }: ActionFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const formData = await request.formData()
  await checkHoneypot(formData)

  const submission = await parseWithZod(formData, {
    schema: InvalidCodeSchema.superRefine(async (data, ctx) => {
      const user = await prisma.user.findFirst({
        where: { email: data.email },
        select: { id: true },
      })

      if (!user) {
        ctx.addIssue({
          path: ['email'],
          code: z.ZodIssueCode.custom,
          message: 'errors.user.email.notFound',
        })
        return
      }
    }),
    async: true,
  })

  if (submission.status !== 'success') {
    return data(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { email } = submission.value

  // User must exist for password reset flow
  const user = await prisma.user.findFirstOrThrow({
    where: { email },
    select: { email: true, firstName: true },
  })

  const verifyUrl = await prepareVerification({
    period: 10 * 60, // 10 minutes
    request,
    type: 'reset-password',
    email,
  })

  const response = await sendResetPasswordEmail({
    email: user.email,
    firstName: user.firstName,
    t,
    verifyUrl: verifyUrl.toString(),
    baseUrl: getDomainUrl(request),
  })

  if (response.status === Status.Success) {
    return { status: Status.Success, submission }
  } else {
    return data(
      { result: submission.reply({ formErrors: [response.error.message] }) },
      { status: 500 },
    )
  }
}

export default function InvalidCode() {
  const invalidCode = useFetcher<typeof action>()
  const { t } = useTranslation()

  return (
    <InvalidCodeForm
      fetcher={invalidCode}
      instructionsText={t('invalidCode.instructionsPasswordReset')}
      emailSuccessfullySentText={t('passwordReset.emailSuccessfullySent')}
    />
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
